var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { createStyles, makeStyles } from '@hawkins/styles';
export function menuItemStyles(theme) {
    var _a, _b;
    var hawkins = theme.hawkins, hawkinsUtils = theme.hawkinsUtils, hawkinsVariables = theme.hawkinsVariables;
    var iconDim = hawkinsVariables.sizings.size1;
    var imageBorderRadius = hawkinsVariables.borderRadiusValues.borderRadius0 / 2;
    var imageDim = hawkinsVariables.sizingValues.size1;
    return createStyles({
        /** Class name of the caret. */
        caret: {
            color: hawkins.icon.fillRegular,
        },
        /** Class name of the element containing the caret. */
        caretContainer: {
            gridArea: 'caret',
            paddingLeft: hawkinsVariables.spacings.space2,
        },
        /** Class name of the icon. */
        icon: {},
        /** Class name of the element containing the item icon or image. */
        iconContainer: {
            '& > $icon': {
                color: hawkins.icon.fillRegular,
                height: iconDim,
                width: iconDim,
            },
            gridArea: 'icon',
            paddingRight: hawkinsVariables.spacings.space2,
            position: 'relative',
        },
        /** Class name of the icon spacer element. */
        iconSpacer: {
            width: imageDim,
        },
        /** Class name of the image. */
        image: {
            borderRadius: imageBorderRadius,
            height: imageDim,
            width: imageDim,
        },
        /** Class name of the image fallback element. */
        imageFallback: {
            alignItems: 'center',
            backgroundColor: hawkins.ui.backgroundColor5,
            borderRadius: imageBorderRadius,
            display: 'flex',
            height: imageDim,
            justifyContent: 'center',
            padding: imageDim / 8,
            width: imageDim,
        },
        /** Class name of the image fallback text. */
        imageFallbackText: {
            color: hawkins.persistent.textColorWhiteRegular,
            fontSize: hawkinsVariables.fontSizeValues.fontSize0 - 2,
        },
        /** Class name of the element containing the label. */
        labelContainer: {
            gridArea: 'label',
            // Force the component to have a minimum height of 40px.
            minHeight: 20,
            // Allow the label to be truncatable.
            overflow: 'hidden',
        },
        /** Class name of the label container when the component is compact. */
        labelContainerCompact: {
            // Force the component to have a minimum height of 32px.
            minHeight: 'auto',
        },
        /** Class name of the nested menu element. */
        nestedMenu: {
            margin: "0 ".concat(hawkins.focus.borderWidthRegular),
            maxHeight: '100vh',
            overflowY: 'auto',
            padding: hawkinsVariables.spacingValues.space0 / 2,
        },
        /** Class name of the `Popper` containing the nested menu. */
        nestedMenuPopper: {
            zIndex: hawkinsVariables.zIndexes.dropdown,
        },
        /** Class name of the root element. */
        root: (_a = {
                color: hawkins.textColor.textColorRegular,
                alignItems: 'center',
                display: 'grid',
                gridTemplate: "\n        \"selection-icon icon label caret\" min-content\n        \". . subtitle caret\" min-content\n        / min-content min-content 1fr min-content\n      ",
                // Prevent inherited line-height from messing up alignment within the CSS grid.
                lineHeight: 'normal',
                padding: "".concat((hawkinsVariables.spacingValues.space2 * 5) / 6, "px ").concat(hawkinsVariables.spacings.space2),
                // The root element needs to be positioned if it contains a <NotificationBadge>, which is absolutely positioned.
                position: 'relative',
                // Prevent an underline from appearing when the component functions as a link.
                textDecoration: 'none'
            },
            _a[hawkinsUtils.getFocusRingedElementSelector('&', '::after')] = __assign(__assign({}, hawkinsUtils.getFocusRingPosition(1)), { bottom: 0, top: 0 }),
            _a['&:visited'] = { color: hawkins.textColor.textColorRegular },
            _a['& $selectionIconMultiSelected'] = { color: hawkins.interactive.backgroundColorRegular },
            _a['& $selectionIconMultiUnselected'] = { color: hawkins.ui.borderColorLight },
            _a['& $selectionIconSingle'] = { color: hawkins.icon.fillRegular },
            _a),
        /** Class name of the root element when the component is clickable (i.e., has an `onClick` callback OR is a link). */
        rootClickable: {
            cursor: 'pointer',
            '&$rootDisabled': { cursor: 'not-allowed' },
        },
        /** Class name of the root element when the component is compact. */
        rootCompact: {
            padding: "".concat(hawkinsVariables.spacingValues.space0 * 1.75, "px ").concat(hawkinsVariables.spacingValues.space1 * 1.25, "px"),
        },
        /** Class name of the root element when the component is disabled. */
        rootDisabled: {
            '& $caret, & $icon': { color: hawkins.icon.fillXlight },
            '& $image, & $imageFallback': { opacity: 0.4 },
            '& $selectionIconMultiSelected': { color: '#313e6f' },
            '& $selectionIconMultiUnselected': { color: hawkins.ui.borderColorXlight },
            '& $selectionIconSingle': { color: hawkins.icon.fillXlight },
        },
        /** Class name of the root element when the component is not disabled. */
        rootEnabled: {},
        /** Class name of the root element when the component is hoverable (i.e., is clickable OR has a nested menu). */
        rootHoverable: {
            '&$rootEnabled': (_b = {},
                _b[hawkinsUtils.getHoveredElementSelector('&')] = {
                    backgroundColor: hawkins.state.backgroundColorHover,
                    '& $selectionIconMultiSelected': { color: hawkins.interactive.backgroundColorHover },
                    '& $selectionIconMultiUnselected': { color: hawkins.ui.borderColorRegular },
                },
                _b),
        },
        /** Class name of the selection icon. */
        selectionIcon: {
            // Used for the `selectionIconBackdrop` hack.
            zIndex: 1,
        },
        selectionIconBackdrop: {
            backgroundColor: hawkins.persistent.backgroundColorWhiteRegular,
            borderRadius: '50%',
            height: imageDim,
            position: 'absolute',
            width: imageDim,
        },
        selectionIconBackdropDisabled: {
            backgroundColor: hawkins.persistent.backgroundColorWhiteXlight,
        },
        /** Class name of the element containing the selection icon. */
        selectionIconContainer: {
            alignItems: 'center',
            display: 'flex',
            gridArea: 'selection-icon',
            position: 'relative',
            width: imageDim * 1.75,
        },
        /** Class name of the multi-select icon when the component is selected. */
        selectionIconMultiSelected: {},
        /** Class name of the multi-select icon when the component is not selected. */
        selectionIconMultiUnselected: {},
        /** Class name of the single-select icon. */
        selectionIconSingle: {},
        /** Class name of the element containing the item subtitle. */
        subtitleContainer: {
            gridArea: 'subtitle',
            // Force the component to have a minimum height of 56px.
            minHeight: hawkinsVariables.sizings.size1,
            // Allow the subtitle to be truncatable.
            overflow: 'hidden',
        },
    });
}
export var useMenuItemStyles = makeStyles(menuItemStyles, { name: 'Hawkins-MenuItem' });
